import React, { MouseEventHandler, useMemo } from 'react';
import { useHideOverflowing } from 'shared/hooks/useHideOverflowing';
import {
  Connection,
  ConnectionType,
  Organization,
  ProviderType,
  SourceWithRoutes,
} from 'shared/types/ObjectTypes';
import {
  CopyToClipboard,
  RefreshSpinner,
} from 'shared/components/ui/ProjectIcons';
import { toast } from 'react-toastify';
import NoResults from 'shared/components/ui/NoResults';
import Pagination from 'shared/components/ui/Pagination';
import { page_size } from 'shared/api/apiUtils';
import { useTranslation } from 'react-i18next';
import { useTimezone } from 'shared/context/TimezoneContext';
import { formatSourceDatetime } from './SourceHelperFunctions';
import {
  tableClasses,
  listPageClasses,
  paginationClasses,
  filterClasses,
  skeletonClasses,
  buttonClasses,
} from 'styles/tailwindClassnames';
import GlobalSearch from 'shared/components/inputs/GlobalSearch';
import SelectInput from 'shared/components/inputs/SelectInput';
import SortByDropdown, {
  SortOption,
} from 'shared/components/inputs/SortByDropdown';
import {
  SortableHeading,
  ariaSortFor,
  sortDirectionFor,
} from 'shared/components/ui/SortArrow';

// Orderings offered by the Sort By dropdown — SourcesPage validates persisted
// filter state against this list (old column-header sorts saved values like
// integration__id that the dropdown doesn't offer).
export const sourceSortValues = [
  'external_id',
  '-external_id',
  '-created_at',
  'created_at',
];

type SourcesListProps = {
  sources: SourceWithRoutes[];
  totalCount: number;
  take: number;
  offset: number;
  onPageClicked: MouseEventHandler;
  loading: boolean;
  organizations: Organization[];
  loadingOrganizations: boolean;
  destinationTypes: ConnectionType[];
  loadingDestinationTypes: boolean;
  destinationUrls: string[];
  loadingDestinationUrls: boolean;
  providerTypes: ProviderType[];
  loadingProviderTypes: boolean;
  globalSearchStr: string;
  onChangeGlobalSearch: (value: string) => void;
  onRefresh: () => void;
  onChangeOwner: Function;
  ownerFilterValue: any;
  onChangeEndpoint: Function;
  endpointFilterValue: any;
  onChangeProviderType: Function;
  providerTypeFilterValue: any;
  onChangeDestinationType: Function;
  destinationTypeFilterValue: any;
  ordering: string;
  onChangeSortBy: (selected: SortOption) => void;
  onViewDetails: Function;
  onViewRouteDetails: Function;
};

const SourcesList = ({
  sources,
  totalCount,
  take,
  offset,
  onPageClicked,
  loading,
  organizations,
  loadingOrganizations,
  destinationTypes,
  loadingDestinationTypes,
  destinationUrls,
  loadingDestinationUrls,
  providerTypes,
  loadingProviderTypes,
  globalSearchStr,
  onChangeGlobalSearch,
  onRefresh,
  onChangeOwner,
  ownerFilterValue,
  onChangeEndpoint,
  endpointFilterValue,
  onChangeProviderType,
  providerTypeFilterValue,
  onChangeDestinationType,
  destinationTypeFilterValue,
  ordering,
  onChangeSortBy,
  onViewDetails,
  onViewRouteDetails,
}: SourcesListProps) => {
  // Phones: a control that does not fully fit in its row is hidden, not cut in half.
  const searchRowRef = useHideOverflowing<HTMLDivElement>();
  const filterRowRef = useHideOverflowing<HTMLDivElement>();
  const { t } = useTranslation();
  const { timezone } = useTimezone();

  const sortOptions: SortOption[] = useMemo(
    () => [
      { label: t('External ID (A-Z)'), value: 'external_id' },
      { label: t('External ID (Z-A)'), value: '-external_id' },
      { label: t('Newest First'), value: '-created_at' },
      { label: t('Oldest First'), value: 'created_at' },
    ],
    [t],
  );

  const hasActiveFilters = !!(
    globalSearchStr ||
    endpointFilterValue ||
    ownerFilterValue ||
    providerTypeFilterValue ||
    destinationTypeFilterValue
  );

  const clearAllFilters = () => {
    onChangeGlobalSearch('');
    onChangeEndpoint(null);
    onChangeOwner(null);
    onChangeProviderType(null);
    onChangeDestinationType(null);
  };

  return (
    <div className="transition-all duration-300">
      <div className={listPageClasses.band}>
        <div className={listPageClasses.bandRow}>
          <h1
            data-testid="sources_page_header"
            className={listPageClasses.title}
          >
            {t('Sources')}
          </h1>
        </div>
      </div>
      <div className={listPageClasses.body}>
        <div className="mx-auto max-w-list-table">
          <div className="w-full">
            {/* Search row */}
            <div
              ref={searchRowRef}
              className="mb-3 flex flex-row flex-nowrap items-end gap-3 overflow-x-clip"
            >
              <GlobalSearch
                onChangeGlobalSearch={onChangeGlobalSearch}
                placeholder={t('Search')}
                savedString={globalSearchStr}
              />
              <SortByDropdown
                options={sortOptions}
                ordering={ordering}
                onChangeSortBy={onChangeSortBy}
              />
              <button
                type="button"
                onClick={() => onRefresh()}
                aria-label={t('Refresh list')}
                disabled={loading}
                className={`mb-2 ml-auto flex h-9 w-8 cursor-pointer items-center justify-center self-end ${loading ? 'origin-center animate-spin' : ''} transition-colors duration-300`}
              >
                <RefreshSpinner />
              </button>
              {hasActiveFilters && (
                <button
                  className={`${buttonClasses.clearFilters} mb-2 whitespace-nowrap`}
                  onClick={clearAllFilters}
                >
                  {t('Clear All Filters')}
                </button>
              )}
            </div>
            {/* Filter dropdowns row */}
            <div
              ref={filterRowRef}
              className="mb-3 flex flex-row flex-nowrap gap-3 overflow-x-clip"
            >
              {/* Destination */}
              <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
                <span className={filterClasses.label}>{t('Destination')}</span>
                <SelectInput
                  name="destination_url__in"
                  options={destinationUrls.map((url) => {
                    let hostname = url;
                    try {
                      hostname = new URL(url).hostname;
                    } catch {}
                    return { label: hostname, value: url };
                  })}
                  value={endpointFilterValue}
                  onChange={onChangeEndpoint}
                  isMulti={false}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={false}
                  isLoading={loadingDestinationUrls}
                  className="w-full"
                  variant="routeFilter"
                />
              </div>
              {/* Workspace */}
              <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
                <span className={filterClasses.label}>{t('Workspace')}</span>
                <SelectInput
                  name="owner__in"
                  options={organizations.map((org) => ({
                    label: org.name,
                    value: org.id,
                  }))}
                  value={ownerFilterValue}
                  onChange={onChangeOwner}
                  isMulti={false}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={false}
                  isLoading={loadingOrganizations}
                  className="w-full"
                  variant="routeFilter"
                />
              </div>
              {/* Provider Type */}
              <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
                <span className={filterClasses.label}>
                  {t('Provider Type')}
                </span>
                <SelectInput
                  name="provider_type__in"
                  options={providerTypes.map((p) => ({
                    label: p.name,
                    value: p.value,
                  }))}
                  value={providerTypeFilterValue}
                  onChange={onChangeProviderType}
                  isMulti={false}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={false}
                  isLoading={loadingProviderTypes}
                  className="w-full"
                  variant="routeFilter"
                />
              </div>
              {/* Destination Type */}
              <div className="flex min-w-[8rem] flex-1 flex-col gap-1">
                <span className={filterClasses.label}>
                  {t('Destination Type')}
                </span>
                <SelectInput
                  name="destination_type__in"
                  options={destinationTypes.map((d) => ({
                    label: d.name,
                    value: d.value,
                  }))}
                  value={destinationTypeFilterValue}
                  onChange={onChangeDestinationType}
                  isMulti={false}
                  isClearable={true}
                  isSearchable={true}
                  isDisabled={false}
                  isLoading={loadingDestinationTypes}
                  className="w-full"
                  variant="routeFilter"
                />
              </div>
            </div>
            {/* Table */}
            <div className="flex w-full flex-col">
              <table className={tableClasses.fixedContainer}>
                <thead className={tableClasses.header}>
                  <tr>
                    <th
                      className={`${tableClasses.headerCell} w-[28%]`}
                      aria-sort={ariaSortFor(
                        sortDirectionFor(ordering, 'external_id'),
                      )}
                    >
                      <SortableHeading
                        direction={sortDirectionFor(ordering, 'external_id')}
                      >
                        {t('External ID')}
                      </SortableHeading>
                    </th>
                    <th className={`${tableClasses.headerCell} w-[25%]`}>
                      {t('Route')}
                    </th>
                    <th className={`${tableClasses.headerCell} w-[25%]`}>
                      {t('Destinations')}
                    </th>
                    <th
                      className={`${tableClasses.headerCell} w-[22%]`}
                      aria-sort={ariaSortFor(
                        sortDirectionFor(ordering, 'created_at'),
                      )}
                    >
                      <SortableHeading
                        direction={sortDirectionFor(ordering, 'created_at')}
                      >
                        {t('Created At')}
                      </SortableHeading>
                    </th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody className="text-body-sm text-ui-text-primary-text">
                    {[...Array(8)].map((_, i) => (
                      <tr
                        key={i}
                        className={tableClasses.row}
                        data-testid="loading"
                      >
                        <td className={tableClasses.cell}>
                          <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                        </td>
                        <td className={tableClasses.cell}>
                          <div className={`h-4 w-40 ${skeletonClasses.base}`} />
                        </td>
                        <td className={tableClasses.cell}>
                          <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                        </td>
                        <td className={tableClasses.cell}>
                          <div className={`h-4 w-24 ${skeletonClasses.base}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody className="text-body-sm text-ui-text-primary-text">
                    {sources.map((src: any) => {
                      return (
                        <tr key={src.source.id} className={tableClasses.row}>
                          <td className={tableClasses.cell}>
                            <span
                              className="block truncate"
                              title={src.source.external_id}
                            >
                              {src.source.external_id}
                            </span>
                          </td>
                          <td className={tableClasses.cell}>
                            <span
                              className="block cursor-pointer truncate text-ui-cta-primary transition-colors duration-300 hover:underline"
                              title={src.source.provider.name}
                              onClick={() =>
                                onViewRouteDetails(src.source.provider.id)
                              }
                            >
                              {src.source.provider.name}
                            </span>
                          </td>
                          <td className={tableClasses.cell}>
                            {src.source.destinations.map((dest: Connection) => {
                              let dest_url: string | URL;
                              try {
                                dest_url = new URL(dest.base_url).hostname;
                              } catch (err) {
                                dest_url = '';
                              }
                              return (
                                <div
                                  key={dest.id}
                                  className="flex min-w-0 items-center gap-2"
                                >
                                  <span
                                    className="block cursor-pointer truncate text-ui-cta-primary transition-colors duration-300 hover:underline"
                                    title={String(dest_url)}
                                    onClick={() => onViewDetails(dest.id)}
                                  >
                                    {dest_url}
                                  </span>
                                  {dest.base_url && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(
                                          dest.base_url,
                                        );
                                        toast.info(
                                          `${t('URL')} ${t('copied to clipboard')}`,
                                          {
                                            position: toast.POSITION.TOP_RIGHT,
                                            autoClose: 2000,
                                          },
                                        );
                                      }}
                                      className="flex flex-shrink-0 items-center justify-center opacity-50 transition-opacity hover:opacity-100"
                                      aria-label={t('Copy to clipboard')}
                                    >
                                      <CopyToClipboard />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </td>
                          <td className={tableClasses.cell}>
                            <span className="block truncate">
                              {formatSourceDatetime(
                                src.source.created_at,
                                timezone,
                              )}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
                {!loading && sources.length === 0 && (
                  <tbody>
                    <tr>
                      <td colSpan={4} className="py-16">
                        <NoResults
                          headerMsg={t('No Results Found')}
                          labelMsg={`${t('You may need to adjust your search or filters to find what you are looking for')}.`}
                        />
                      </td>
                    </tr>
                  </tbody>
                )}
              </table>
              {totalCount > page_size && (
                <Pagination
                  showText={true}
                  handlePageClick={onPageClicked}
                  pageCount={Math.ceil(totalCount / take)}
                  resultStart={offset + 1}
                  resultEnd={
                    offset + take < totalCount ? offset + take : totalCount
                  }
                  initialPage={Math.floor(offset / take)}
                  totalCount={totalCount}
                  take={take}
                  loading={loading}
                  mainclassname={paginationClasses.container}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourcesList;
